'use client';

import { css, cx } from '@/styled-system/css';
import ListingSearch from '@/src/components/ListingSearch';
import type { AreaResponse } from '@/src/lib/api';
import { useIsDesktop } from '@/src/lib/hooks/ui/useIsDesktop';
import { searchWrapper } from '../../_shell/searchListingSearch.styles';
import { usePathname } from '@/src/i18n/navigation';
import { isHdpPathname } from '@/src/lib/utils/listingSlugUtils';
import { useSrpViewMode } from './ViewModeContext';
import { SrpSaveSearchButton } from './SrpSaveSearchButton';
import type { SaveSearchTriggerLocation } from '@/src/analytics/hotpads/events';
import type { ViewMode } from '@/src/lib/hooks/ui/useViewMode';

type Props = {
  currentAreaFallback?: AreaResponse | null;
  variant?: 'sticky' | 'floating' | 'header';
};

export function getSaveSearchTriggerLocation(
  variant: NonNullable<Props['variant']>,
  viewMode: ViewMode,
  isDesktop: boolean,
): SaveSearchTriggerLocation {
  if (variant === 'floating') return 'search_results_map';
  if (viewMode === 'feed' && !isDesktop) return 'search_results_feed';
  return 'search_results_list';
}

const stickyWrapper = css({
  background: 'bg.neutral',
  paddingTop: 'tighter',
  paddingBottom: 'tighter',
  paddingInline: 'default',
  position: 'sticky',
  top: 0,
  zIndex: 'zIndexStep',
  lg: {
    display: 'none',
  },
});

const floatingWrapper = css({
  width: '100%',
  background: 'transparent',
  position: 'static',
});

const headerWrapper = css({
  display: { base: 'none', lg: 'block' },
  width: '100%',
  // eslint-disable-next-line hotpads/no-magic-numbers -- Figma's desktop search bar is 824px wide
  maxWidth: '51.5em',
  minWidth: 0,
});

const headerSearchWrapper = css({
  width: '100%',
  boxSizing: 'border-box',
  paddingBlock: 'tightest',
  paddingInline: 'tightest',
  borderRadius: 'pill',
  backgroundColor: 'bg.action.neutral.alt.default',
  borderStyle: 'solid',
  borderColor: 'border.action.neutral.default',
  borderWidth: { base: 'default', _dark: 'zero' },
});

const feedModeStickyWrapper = css({
  '&[data-search-bar-variant="sticky"][data-srp-feed-mode="true"]': {
    position: 'static',
    lg: {
      display: 'none',
    },
  },
});

export function SrpListingSearchBar({ currentAreaFallback, variant = 'sticky' }: Props) {
  const pathname = usePathname();
  const { viewMode } = useSrpViewMode();
  const isDesktop = useIsDesktop();
  const isFeedMode = viewMode === 'feed' && !isDesktop;
  const saveSearchTriggerLocation = getSaveSearchTriggerLocation(variant, viewMode, isDesktop);
  const isHdp = isHdpPathname(pathname);
  const wrapperClass = variant === 'floating' ? floatingWrapper : variant === 'header' ? headerWrapper : stickyWrapper;
  const searchSurfaceClass = variant === 'header' ? headerSearchWrapper : searchWrapper;

  if (variant === 'header' && isHdp) {
    return null;
  }

  return (
    <div
      className={cx(wrapperClass, feedModeStickyWrapper)}
      data-search-bar-variant={variant}
      data-srp-feed-mode={isFeedMode ? 'true' : 'false'}
      data-srp-component="search-bar"
    >
      <div className={searchSurfaceClass} data-srp-component="search-bar-surface">
        <ListingSearch
          size="sm"
          nearMeArea={currentAreaFallback}
          compactControls={variant !== 'header'}
          // Pathname, not the seeded target: the target lands in an effect, so
          // gating on it would drop the button from the server-rendered bar.
          // Every bar names itself: all of them are mounted at once and CSS
          // picks the visible one, so there is no bare selector to give out.
          trailingAction={
            isHdp ? undefined : (
              <SrpSaveSearchButton testIdSuffix={variant} triggerLocation={saveSearchTriggerLocation} />
            )
          }
        />
      </div>
    </div>
  );
}
